import { type MouseEvent as ReactMouseEvent, type ReactNode } from 'react';
import {
    Stepper as PrimeStepper,
    type StepperChangeEvent,
    type StepperProps as PrimeStepperProps,
} from 'primereact/stepper';
import { StepperPanel as PrimeStepperPanel } from 'primereact/stepperpanel';
import './Stepper.scss';

export { type StepperChangeEvent } from 'primereact/stepper';
export const StepperPanel = PrimeStepperPanel;

export type StepperVariant = 'default' | 'pill';

export interface StepperProps extends Omit<PrimeStepperProps, 'className'> {
    variant?: StepperVariant;
    className?: string;
    children?: ReactNode;
}

export function Stepper({
    variant = 'default',
    className,
    ...props
}: StepperProps): ReactNode {
    const variantClass = variant === 'pill' ? 'ics-stepper-pill' : '';
    const combinedClassName = [variantClass, className]
        .filter(Boolean)
        .join(' ');

    return <PrimeStepper className={combinedClassName} {...props} />;
}

export interface PillStepperItem {
    id: string;
    title: string;
    completed?: boolean;
    reached?: boolean;
    nextAccessible?: boolean;
    invalid?: boolean;
    disabled?: boolean;
    canInteract?: boolean;
    separatorReached?: boolean;
    separatorNextAccessible?: boolean;
}

export interface PillStepperProps {
    activeStep: number;
    items: PillStepperItem[];
    onChangeStep: (event: StepperChangeEvent) => void;
    onHeaderClickCapture?: (event: ReactMouseEvent<HTMLElement>) => void;
    className?: string;
}

export function PillStepper({
    activeStep,
    items,
    onChangeStep,
    onHeaderClickCapture,
    className,
}: PillStepperProps): ReactNode {
    return (
        <Stepper
            variant="pill"
            className={className}
            pt={{
                nav: {
                    className: 'mb-3',
                    onClickCapture: onHeaderClickCapture,
                },
                panelContainer: {
                    style: { display: 'none' },
                },
            }}
            activeStep={activeStep}
            onChangeStep={onChangeStep}
            linear={false}
        >
            {items.map((item, index) => {
                const canInteract = item.canInteract ?? !item.disabled;

                return (
                    <StepperPanel
                        key={item.id}
                        header={item.title}
                        pt={{
                            header: {
                                'data-ics-step-index': index,
                                'data-ics-reached': item.reached,
                                'data-ics-completed': item.completed,
                                'data-ics-next-accessible': item.nextAccessible,
                                'data-ics-invalid': item.invalid,
                                'data-ics-disabled': item.disabled,
                            },
                            action: {
                                tabIndex: canInteract ? 0 : -1,
                                'aria-disabled': canInteract ? 'false' : 'true',
                                disabled: !canInteract,
                            },
                            separator: {
                                'data-ics-reached': item.separatorReached,
                                'data-ics-next-accessible':
                                    item.separatorNextAccessible,
                            },
                        }}
                    />
                );
            })}
        </Stepper>
    );
}
